import { useEffect, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box"
import DoneAllIcon from '@mui/icons-material/DoneAll';

import { useTranslation, withTranslation } from "react-i18next";
import { isEmpty, isEqual } from "lodash";
import { grey } from "@mui/material/colors";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import calendarGroupService from "@plannel/services/data/calendar-group-service";
import CalendarModal from "@plannel/components/modal/CalendarModal"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;
const { calendarType, cycleUoms } = CodeUtil;
const CalendarGroupModal = ({ modalOpen, setModalOpen, setSnackInfo, editedCell, calType, readOnly = true }) => {
  const { t } = useTranslation();

  const gridRef = useRef();
  const [calendarGroups, setCalendarGroups] = useState([]);

  const searchCode = useRef("");

  const [calendarModalOpen, setCalendarModalOpen] = useState(false);
  const selectedCalendar = useRef();

  const calendarTypeFormatter = params => {
    const { value } = params;

    return calendarType?.[value] ? GridUtils.gridValueL10N(calendarType[value]) : value;
  }

  const editable = () => {
    return readOnly ? false : true;
  }

  const columnDefs = [
    {
      headerName: "calendarGrpName", field: "name", width: 200, cellRenderer: "agGroupCellRenderer", editable
    },
    {
      headerName: "calendarType", field: "calType", width: 150,
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      valueFormatter: calendarTypeFormatter,
      cellEditorParams: {
        valueFormatter: calendarTypeFormatter,
        values: Object.keys(calendarType)
      },
      editable
    },
    { headerName: "descTxt", field: "descTxt", width: 400, editable, suppressSizeToFit: true },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn", "nonEditableColumn"], hide: true },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, hide: true },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn",hide: true },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime,hide: true },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", hide: true}
  ];

  const detailEnabled = params => {
    return params.calendars ? params.calendars.length > 0 : false;
  }

  const disabledColor = { background: grey[300], opacity: 0.5 };

  const setWomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "M") && !data.domObj?.onDates) {
      return {};
    }

    return disabledColor;
  }

  const setDomDisabledColor = params => {
    const { data } = params;
    if (isEqual(data.cycleCd, "W") || (isEqual(data.cycleCd, "M") && !data.domObj?.onDates)) {
      return {};
    }

    return disabledColor;
  }

  const valueFormatter = (params) => {
    return params.value?.name ? params.value.name : "";
  }

  const cycleCalendarColumnDefs = [
    { headerName: "calendarName", field: "name", type: ["nonEditableColumn"], width: 200 },
    {
      headerName: "repeatEvery", groupId: "repeatEvery",
      children: [
        {
          headerName: "", field: "intervalVal", type: ["numberColumn", "nonEditableColumn"], width: 90,
          cellStyle: params => {
            const { data } = params;
            if (isEqual(data.cycleCd, "S")) {
              return disabledColor;
            }
            return {};
          }
        },
        {
          headerName: "", field: "cycleCd", type: ["nonEditableColumn"], width: 100,
          valueFormatter: params => {
            const { value } = params;
            const uomIndex = cycleUoms.findIndex(uom => isEqual(uom.value, value));
            if (uomIndex > -1) {
              const cycleUom = cycleUoms[uomIndex];
              return t(cycleUom.text);
            }

            return value;
          }
        }
      ]
    },
    {
      headerName: "period", groupId: "period",
      children: [
        { headerName: "startDate", field: "startDate", colId: "startDate", type: ["nonEditableColumn"], width: 120 },
        { headerName: "endDate", field: "endDate", colId: "endDate", type: ["nonEditableColumn"], width: 120 }
      ]
    },
    { headerName: "calendarGrpName", field: "calendarGroup", type: ["nonEditableColumn"], width: 150, valueFormatter },
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 200 },
    // Month of Year
    {
      headerName: "monthOfYear", field: "domObj", type: ["nonEditableColumn"], width: 130,
      valueFormatter: params => {
        const { value } = params;
        if (value?.onMonth) {
          return value.onMonth + 1;
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y")) {
          return {};
        }
        return disabledColor;
      }
    },
    // Day of Month
    {
      headerName: "dateOfMonth", field: "domObj", type: ["nonEditableColumn"], width: 120,
      valueFormatter: params => {
        const { value } = params;

        if (value?.onDate) {
          return value.onDate;
        } else if (value?.onDates) {
          const dates = value.onDates;
          return dates.join(", ");
        }
        return "";
      },
      cellStyle: params => {
        const { data } = params;
        if (isEqual(data.cycleCd, "Y") || (isEqual(data.cycleCd, "M") && data.domObj?.onDates)) {
          return {};
        }
        return disabledColor;
      }
    },
    // Week of Month
    {
      headerName: "weekOfMonth", groupId: "wom",
      children: [
        { headerName: "first", field: "wom1wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "second", field: "wom2wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "third", field: "wom3wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "fourth", field: "wom4wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
        { headerName: "fifth", field: "wom5wFlg", type: ["booleanColumn", "nonEditableColumn"], width: 85, cellStyle: setWomDisabledColor },
      ]
    },
    // Day of Week
    {
      headerName: "dayOfWeek", groupId: "dow",
      children: [
        { headerName: "mon", field: "dowMonFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "tue", field: "dowTueFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "wed", field: "dowWedFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "thu", field: "dowThuFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "fri", field: "dowFriFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "sat", field: "dowSatFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
        { headerName: "sun", field: "dowSunFlg", type: ["booleanColumn", "nonEditableColumn"], width: 75, cellStyle: setDomDisabledColor },
      ]
    },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn", "nonEditableColumn"], width: 80 }
  ];

  const onCellDoubleClicked = params => {
    selectedCalendar.current = params.data;
    setCalendarModalOpen(true);
  }

  const calendarGridDefaultSettings = DefaultGridSetting({});

  const calendarGrid = {
    detailGridOptions: {
      ...calendarGridDefaultSettings,
      defaultColDef: {
        ...calendarGridDefaultSettings.defaultColDef,
        suppressHeaderMenuButton: true,
      },
      suppressRowClickSelection: true,
      onCellDoubleClicked,
      columnDefs: GridUtils.getColumnDefs(cycleCalendarColumnDefs),
    },
    getDetailRowData: params => {
      params.successCallback(params.data.calendars);
    }
  };

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};
    searchFilters.activeFlg = true;

    if (searchCode.current) {
      searchFilters.calendarGrpName = searchCode.current;
    }

    if (calType) {
      searchFilters.calendarType = calType;
    }

    params.searchFilters = searchFilters;

    return params;
  }

  const retrieveCalendarGroup = () => {
    const params = getRequestParams();
    calendarGroupService.getAll(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        GridUtils.setRowData(gridRef.current.api, results);
        setCalendarGroups(results);
      })
      .catch(e => {
        debug(e);
      });
  }

  const selectCalendarGroup = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    const value = isEmpty(selectedRows) ? null : selectedRows[0];

    if (editedCell) {
      editedCell.node.setDataValue(editedCell.column, value);
    }

    setModalOpen(false);
  }

  const onFirstDataRendered = params => {
    if (params.api && editedCell?.value) {
      params.api.forEachNode(node => {
        const { name } = node.data;

        if (isEqual(editedCell.value?.name, name)) {
          node.setSelected(true);
        }
      });

      GridUtils.focusOnSelectedRow(params.api);
    }
  }

  const onSelectionChanged = params => {
    const selected = params.api.getSelectedNodes();
    if (selected.length > 1) {
      params.api.deselectAll();
      selected[selected.length - 1].setSelected(true);
    }
  }

  const handleClose = () => {
    setModalOpen(false);
  }

  useEffect(() => {
    if (modalOpen) {
      retrieveCalendarGroup();
    }

    return () => {
      setCalendarGroups([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  return (
    <>
      {/* selectCalendarGroup 은 그리드 셀에 값을 넣고 닫을 뿐 저장하지 않는다 —
          save 가 아니라 set + DoneAll (CalendarModal 그리드 모드의 같은 성격 버튼과 맞춤). */}
      <BaseConfigModal
        open={modalOpen}
        onClose={handleClose}
        title={t("calendarGrp")}
        onSave={selectCalendarGroup}
        saveLabel={t("set")}
        saveIcon={<DoneAllIcon />}
        width={932}
      >
          <TextFieldSearch sx={{ width: 250 }} label={"calendarGrpName"} searchCode={searchCode} retrieve={retrieveCalendarGroup} />
          <Box className="ag-theme-balham" sx={{ height: "40vh" }} mt={1} >
            <AgGridReact
              ref={gridRef}
              rowData={calendarGroups}
              columnDefs={GridUtils.getColumnDefs(columnDefs)}
              {...DefaultGridSetting({})}
              masterDetail
              autoSizeStrategy={{
                type: "fitCellContents",
                defaultMaxWidth: 1000,
                defaultMinWidth: 50,
              }}
              rowSelection={
                {
                  mode: 'singleRow',
                  enableClickSelection: true,
                }
              }
              isRowMaster={detailEnabled}
              detailCellRendererParams={calendarGrid}
              onFirstDataRendered={onFirstDataRendered}
              onSelectionChanged={onSelectionChanged}
            />
          </Box>
      </BaseConfigModal>
        <CalendarModal
          readOnly
          modalOpen={calendarModalOpen}
          setModalOpen={setCalendarModalOpen}
          selectedCycle={selectedCalendar.current}
        />
    </>
  );
};

export default withTranslation()(CalendarGroupModal);